import { useState } from 'react';
import { Episode } from '@/types/podcast';
import { Button } from '@/components/ui/button';
import { HighlightText } from '@/components/common/HighlightText';
import { cn } from '@/lib/utils';
import { Play, Pause, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

interface MatchedEpisodeCardProps {
    episode: Episode;
    highlightKeywords: string[];
    isCurrent: boolean;
    isPlaying: boolean;
    onPlay: () => void;
    formatDate: (date?: string) => string;
}

export function MatchedEpisodeCard({ 
    episode, 
    highlightKeywords, 
    isCurrent, 
    isPlaying, 
    onPlay,
    formatDate 
}: MatchedEpisodeCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-yellow-50/50 rounded-xl border border-yellow-200/60 p-4 transition-all hover:bg-yellow-50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
            <div className="flex items-start gap-4">
                <Button
                    size="icon"
                    className={cn(
                        "rounded-full h-10 w-10 shrink-0 shadow-sm bg-white border border-yellow-200 hover:bg-yellow-100 text-ambert-700",
                        isCurrent && isPlaying && "bg-amber-500 text-white border-amber-500 hover:bg-amber-600"
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        onPlay();
                    }}
                >
                    {isCurrent && isPlaying ? (
                        <Pause className="h-4 w-4" />
                    ) : (
                        <Play className="h-4 w-4 ml-0.5 text-amber-600" />
                    )}
                </Button>
                
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-2">
                        <h3 className="font-semibold text-gray-900 mb-1 leading-snug">
                             <HighlightText text={episode.title} keywords={highlightKeywords} />
                        </h3>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 hover:bg-yellow-100 text-yellow-700"
                        >
                            {isExpanded ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
                        </Button>
                    </div>

                    <div className="flex items-center text-xs text-gray-500 mb-2 gap-3">
                        <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(episode.publishedAt)}
                        </span>
                        {episode.durationFormatted && (
                            <span>{episode.durationFormatted}</span>
                        )}
                    </div>
                    
                    <div className={cn("text-sm text-gray-600", !isExpanded && "line-clamp-2")}>
                         {isExpanded ? (
                             // Show full text with highlights, pass large maxLength
                             <div className="whitespace-pre-wrap">
                                 <HighlightText 
                                    text={episode.description || episode.summary} 
                                    keywords={highlightKeywords} 
                                    maxLength={10000} 
                                 />
                             </div>
                         ) : (
                             // Show truncated text
                             <HighlightText 
                                text={episode.description || episode.summary} 
                                keywords={highlightKeywords} 
                                maxLength={200} 
                             />
                         )}
                    </div>
                     {/* Show visual cue if collapsed text is likely truncated */}
                     {!isExpanded && (episode.description || episode.summary || "").length > 200 && (
                        <div className="mt-1">
                            <span className="text-xs text-amber-600 font-medium hover:underline">Read more</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
